<template lang="pug">
.bar 
  .bar__header
    .bar__label {{ legend?.title }}
    .bar__legend(:style="{ marginRight: `${legendMarginRight}px` }")
      .bar__legend__item(v-for="item in legend?.items", :key="item.title")
        .bar__legend__item__circle(:style="`background-color: ${item.color}`")
        .bar__legend__item__label {{ item.title }}
      .bar__buttons(v-if="canScrollLeft || canScrollRight")
        .bar__buttons-item(:class="scrollLeftClass", @click="scrollChart('left')") 
          q-icon(name="arrow_back_ios")
        .bar__buttons-item(:class="scrollRightClass", @click="scrollChart('right')")
          q-icon(name="arrow_forward_ios")

  apexchart(v-if="isSeriesNonempty", type="bar", :height="height", :options="chartOptions", :series="series")
  .apexchart-empty-value(v-else)
    span {{ gridLocales?.hidden_message }}
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { gridLocales } from "@/services/useLocales";

const props = defineProps({
  /**
   * Дата самого чарта. Имеет несколько обязательных ключей: categories, data и legend
   * Пример: {
   *  categories: [1],
   *  data: [{
   *    name: "Сервисные заявки",
   *    data: [151]
   *  }],
   *  legend: {
   *    items: [{
   *      color: "B5BBC9",
   *      title: "Сервисные заявки"
   *    }],
   *    title: "График сервисных заявок с начала года по всем строениям"
   *  }
   * }
   */
  chartData: { type: Object, required: true, default: () => {} },
  /* Коэфициент длины колонки чарта. Нужен для примерно равной длины столбцов при разных размерах графика. По умолчанию 2 */
  barColumnWidthCoef: { type: Number, default: () => 2 },
  /* Высота бара. Пример: 300 */
  height: { type: Number, default: () => 300 },
  /* Отступ легенды от правого края. Нужно для умещения кнопок с дашборда с позицией absolute. */
  legendMarginRight: { type: Number, default: () => 0 },
  /* Максимальная длина в серии до скролла */
  maxSeries: { type: Number, default: () => 12 },
});

const barColumnWidth = 1.5;

const series = ref([]); // Серии баров отображаемых пользователю внутри его скролла
const fullSeries = ref([]); // Все серии переданные от родителя
const legend = ref([]); // Данные легенды переданные от родителя
const canScrollLeft = ref(true); // Флаг возможности скроллить чарт влево
const canScrollRight = ref(true); // Флаг возможности скроллить чарт вправо
// Текущая позиция скролла. Хранит в себе первый и последний индексы массивов в fullSeries
const currentScrollPosition = ref([]);
const canSeeTooltipOnHover = ref(false); // Флаг возможности отображения тултипа

// Переменная хранящая текущее значение опций чарта
const chartOptions = ref({
  chart: {
    type: "bar", // Тип чарта
    toolbar: {
      show: false, // Блокируем дефолтное отображение тулбара, т.к. используем кастомный
    },
    events: {
      mouseMove: (e, chartContext, config) => {
        // Задаем обработчик события движения курсора по чарту
        if (!loading.value) {
          // Если чарт не загружен, не делаем ничего
          // Ось x хранящая в себе тексты
          const xAxis = chartContext.el.querySelector(".apexcharts-xaxis-texts-g");
          const tooltip = chartContext.el.querySelector(".apexcharts-tooltip"); // Текущий тултип
          const tooltipText = tooltip.querySelector(".column-chart__tooltip");
          const currentTooltipClientRect = tooltip.getBoundingClientRect(); // DOMRect тултипа
          let barIndex = config.dataPointIndex; // Индекс массива текущего бара
          let seriesIndex = config.seriesIndex; // Индекс массива текущей серии
          const tooltipWidth = currentTooltipClientRect?.width; // Длина тултипа
          const tooltipHeight = currentTooltipClientRect?.height; // Высота тултипа

          // Задаем тултипа класс назначающей opacity: 1 !important чтобы он не исчезал при уходе с бара
          tooltip.classList.add("always-show-tooltip");

          // Проверяем не находится ли курсор на текущем тултипе
          const cursorOnTooltip =
            e.x > currentTooltipClientRect?.x &&
            e.x < currentTooltipClientRect?.x + tooltipWidth &&
            e.y > currentTooltipClientRect?.y &&
            e.y < currentTooltipClientRect?.y + tooltipHeight;

          // Функция сброса закрашивания текста на оси X текущего бара
          const resetXAxis = () => {
            xAxis.childNodes.forEach(item => {
              item.style.fill = "var(--header-subtitle-color)";
            });
          };

          if (tooltip) {
            tooltip.style.visibility = "visible"; // Назначаем тултипу visible если тултип существует в DOM

            // Если курсор на позиции тултипа и можно видеть тултип
            if (cursorOnTooltip && canSeeTooltipOnHover.value) {
              // Если у тултипа назначены индексы серии и бара, переназначаем их. Это нужно для
              // продолжения работы функции даже при покидании бара и нахождении на месте тултипа
              if (tooltipText.getAttribute("current-series")) seriesIndex = tooltipText.getAttribute("current-series");
              if (tooltipText.getAttribute("current-point")) barIndex = tooltipText.getAttribute("current-point");
            } else {
              // Если курсор не на позиции тултипа задаем возможность видеть тултип
              canSeeTooltipOnHover.value = true;
            }

            // Если курсор не находится на любом из баров, скрываем тултип и сбрасываем закрашивание оси X
            if (barIndex === -1 || barIndex === series.value[0].data.length) {
              canSeeTooltipOnHover.value = false;
              tooltip.style.visibility = "hidden";
              resetXAxis();
              return;
            }
          }

          // DOM элемент текущего бара
          const currentBar = chartContext.el.querySelector(`.apexcharts-series[data\\:\\realIndex="${seriesIndex}"]`)
            .childNodes[barIndex];
          const barWidth = Number(currentBar.getAttribute("barWidth")); // Длина текущего бара
          // Отступ самого чарта от левой границы экрана
          const chartOffsetX = chartContext.el.querySelector(".apexcharts-graphical").getBoundingClientRect().left;
          // Разница между отступом от левой границы экрана до чарта и левой границы экрана
          // до непосредственно самих графиков. Нужно для вычисления позиции тултипа
          const chartWrapperMargin =
            chartOffsetX - chartContext.el.querySelector(".apexcharts-canvas").getBoundingClientRect().left;
          // Расстояние слева до текущего бара
          const barMarginLeft =
            currentBar.getBoundingClientRect().left -
            chartContext.el.querySelector(".apexcharts-graphical").getBoundingClientRect().left;
          // Отступ тултипа сверху графика. Берется из аттрибута бара в котором лежит отступ от верха
          tooltip.style.marginTop = `${currentBar.getAttribute("cy")}px`;
          // Отступ слева. Высчитывается по формуле суммы всех отступов + половина длины бара
          tooltip.style.marginLeft = `${chartWrapperMargin + barMarginLeft + barWidth / 2}px`;

          // Если тултип не умещается на экране не выходя за его пределы, то переносим отзеркаливаем
          // его по длине и переносим указывающий на бар треугольник под сам бар
          if (window.innerWidth - currentBar.getBoundingClientRect().left < tooltipWidth) {
            tooltip.style.transform = `translate(calc(-100% + ${barWidth}px), -80%)`;
            tooltip.style.setProperty("--arrow-margin", `${tooltipWidth - barWidth}px`);
            tooltip.style.setProperty("--border-radius", "12px 12px 0px 12px");
          } else {
            // Если умещается, то выставляем тултип ровно над баров, а указывающий треугольник посередине
            tooltip.style.setProperty("--arrow-margin", `50%`);
            tooltip.style.transform = `translate(-50%, -80%)`;
            tooltip.style.setProperty("--border-radius", "12px");
          }

          // Если курсор вне бара, сбрасываем закрашивание оси X и скрываем тултип
          if (barIndex === -1 || barIndex === series.value[0].data.length) {
            resetXAxis();
            canSeeTooltipOnHover.value = false;
            tooltip.style.visibility = "hidden";
            // Иначе закрашиваем нужный текст на оси X и задаем тултипа visibility: visible
          } else {
            xAxis.childNodes[barIndex].style.fill = "#0EC262";
            tooltip.style.visibility = "visible";
          }
        }
      },
      mouseLeave: (e, chartContext) => {
        // задаем обработчик события покидания курсора с чарта
        if (!loading.value) {
          // Если чарт загружается ничего не делаем
          const tooltip = chartContext.el.querySelector(".apexcharts-tooltip"); // DOM элемент тултипа
          const xAxis = chartContext.el.querySelector(".apexcharts-xaxis-texts-g"); // DOM оси X

          // Удаляем класс с тултипа задающий ему opacity: 1 !important чтобы он исчез
          tooltip.classList.remove("always-show-tooltip");

          // Удаляем с оси X все закрашивания
          xAxis.childNodes.forEach(item => {
            item.style.fill = "var(--header-subtitle-color)";
          });
        }
      },
    },
  },
  plotOptions: {
    bar: {
      horizontal: false,
    },
  },
  // Скрываем лэйблы даты над барами
  dataLabels: {
    enabled: false,
  },
  // Цвета колонок
  colors: [],
  // Состояния баров. Отключаем действия при hover и active
  states: {
    hover: {
      filter: { type: "none" },
    },
    active: {
      filter: { type: "none" },
    },
  },
  // Скрываем легенду по умолчанию
  legend: {
    show: false,
  },
  // Ось X
  xaxis: {
    // Сами категории
    categories: [],
    // Убираем бордеры
    axisBorder: {
      show: false,
    },
    // Убираем черточки между категориями
    axisTicks: {
      show: false,
    },
    // Убираем разделения между барами при наведении
    crosshairs: {
      show: false,
    },
  },
  // Тултип
  tooltip: {
    // Убираем перемещение тултипа за курсором
    followCursor: false,
    // Задаем статичную позицию тултипа слева-сверху графика, мы его перемещаем в событии mouseMove
    fixed: {
      enabled: true,
      position: "topLeft",
    },
    // Создаем кастомный тултип
    custom: function ({ series, seriesIndex, dataPointIndex, w }) {
      const date = w.config.xaxis.categories[dataPointIndex]; // Текст категории под баром
      const color = w.config.colors[seriesIndex]; // Цвет серии
      let value = series[seriesIndex][dataPointIndex]; // Значение бара

      // Текст в тултипа
      let text = `<div class='column-chart__tooltip', current-series='${seriesIndex}', current-point='${dataPointIndex}'>`;
      text += `<div class='column-chart__tooltip__header'><span class='column-chart__tooltip__circle', style='background-color: ${color}'></span>`;
      text += `<span class='column-chart__tooltip__label'>${value}</span></div>`;
      text += `<p class='column-chart__tooltip__value'>${date}</p></div>`;

      return text;
    },
  },
});

const maxSeriesLength = computed(() => props.maxSeries);
const seriesScrollStep = computed(() => Math.ceil(maxSeriesLength.value / 4));
const fullSeriesLength = computed(() => fullSeries.value?.[0]?.data?.length || 0);

const scrollLeftClass = computed(() => (canScrollLeft.value ? "active" : ""));
const scrollRightClass = computed(() => (canScrollRight.value ? "active" : ""));

const loading = computed(() => props.loading);
const barColumnWidthCoef = computed(() => props.barColumnWidthCoef);
const isSeriesNonempty = computed(() => series.value.some(item => item.data.length > 0));

// Функция назначения параметров чарта
const setChartParams = () => {
  // Назначаем данные пришедшие от родителя в реактивные переменные компонента
  legend.value = props.chartData?.legend;
  chartOptions.value.colors = legend.value.items.map(item => item.color);
  fullSeries.value = props.chartData?.data;

  // Если длина всех серий <= максимально возможной, то назначаем видимые серии на всю длину серий
  if (fullSeriesLength.value <= maxSeriesLength.value) {
    const positionStart = 0;
    const positionEnd = fullSeriesLength.value;
    setNewSeries(positionStart, positionEnd);
  } else {
    // Если длина > максимально возможной, то назначаем видимые серии от 0 до максимально возможной
    const positionStart = 0;
    const positionEnd = maxSeriesLength.value;
    setNewSeries(positionStart, positionEnd);
  }
};

// Назначаем новые серии от fullSeries

const setNewSeries = (start, end) => {
  // Если start < 0, например при скролле на 4 влево, а мы были на 2 и стало -2, назначаем start 0
  if (start < 0) {
    end = end - start;
    start = 0;
  }
  // Если end > 0, например при скролле на 4 вправо, а мы были на предпоследнем
  // и стало на 2 больше допустимого, назначаем end максимальную длину всей серии
  if (end > fullSeriesLength.value) {
    start = start - (end - fullSeriesLength.value);
    end = fullSeriesLength.value;
  }

  // Сбрасываем категории
  chartOptions.value.xaxis.categories = [];
  // Назначаем новые категории в зависимости от start и end
  const newCategories = props.chartData.categories.slice(start, end);
  // Создаем массив сокращенных категорий для отображения снизу для избежания наложения текста. Макс 10 символов
  const newShortCategories = props.chartData.categories.slice(start, end).map(item => {
    let returnedShortCategory = item.toString().slice(0, 10);
    if (item.length > 10) {
      returnedShortCategory += "...";
    }
    return returnedShortCategory;
  });
  // Переназначаем chartOptions на те же значения что и были, кроме измененного xaxis. Если не переназначать на те же что и были, а только менять chartOptions.value.xaxis, apexcharts не перерисовывает график
  chartOptions.value = {
    ...chartOptions.value,
    xaxis: {
      categories: newCategories,
      overwriteCategories: newShortCategories,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      crosshairs: {
        show: false,
      },
    },
  };

  // Длина отображаемого массива категорий
  const chartItemsLength = chartOptions.value.xaxis.categories.length;
  // Количество баров в серии
  const chartItemsInBarLength = fullSeries.value.length;
  let fullBarColumnWidth; // Переменная для хранения ширины бара

  if (window.innerWidth > 1500) {
    // Если размер экрана > 1500px задаем стандартную ширину колонок
    fullBarColumnWidth = chartItemsLength * barColumnWidth * barColumnWidthCoef.value * chartItemsInBarLength;
    // Для экранов размером меньше 1500 задаем значение колонок поменьше на четверть
  } else {
    fullBarColumnWidth = chartItemsLength * (barColumnWidth / 0.75) * barColumnWidthCoef.value * chartItemsInBarLength;
  }

  chartOptions.value.plotOptions.bar.columnWidth = `${fullBarColumnWidth}px`; // Назначаем ширину баров

  currentScrollPosition.value = [start, end]; // Сохраняем текущую позицию скролла
  // Переназначаем серии
  series.value = [];
  fullSeries.value.forEach(item => {
    series.value.push({
      name: item.name,
      data: item.data.slice(start, end),
    });
  });
  // Переназначаем юзабельность кнопок скролла
  start === 0 ? (canScrollLeft.value = false) : (canScrollLeft.value = true);
  end === fullSeriesLength.value ? (canScrollRight.value = false) : (canScrollRight.value = true);
};

// Функция скролла. Принимает текст "right"/"left"

const scrollChart = direction => {
  // Если скроллить нельзя, не выполняет ничего
  if (canScrollRight.value || canScrollLeft.value) {
    // Если можно скролить вправо скроллим вправо на длину скролла
    if (direction === "right" && canScrollRight.value) {
      currentScrollPosition.value = currentScrollPosition.value.map(item => {
        return item + seriesScrollStep.value;
      });
      setNewSeries(currentScrollPosition.value[0], currentScrollPosition.value[1]);
    }
    // Если можно скроллить влево скроллив влево на длину скролла
    if (direction === "left" && canScrollLeft.value) {
      currentScrollPosition.value = currentScrollPosition.value.map(item => {
        return item - seriesScrollStep.value;
      });
      setNewSeries(currentScrollPosition.value[0], currentScrollPosition.value[1]);
    }
  }
};

onMounted(() => {
  setChartParams();
});
</script>

<style lang="scss">
// scoped не используется, т.к. apexcharts рисует свои компоненты вне данного компонента и не видит подставляемые vue атррибуты для scoped
.bar {
  width: 100%;

  background-color: var(--expenses-content-background-color);

  border-radius: 20px;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 24px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }

  .apexchart-empty-value {
    min-height: 315px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--expenses-main-text-color);
  }

  &__legend {
    display: flex;
    flex-direction: row;

    &__item {
      display: flex;
      align-items: center;
      gap: 5px;

      &:not(:last-child) {
        margin-right: 20px;
      }

      &__circle {
        width: 8px;
        height: 8px;

        border-radius: 50%;

        background-color: #5b7aff;
      }
      &__label {
        font-weight: 40;
        font-size: 14px;
        color: #8a9099;
      }
    }
  }

  &__label {
    color: var(--expenses-main-text-color);
    font-size: 20px;
  }
  & .apexcharts-series path {
    clip-path: inset(0 0 0 0 round 15px 15px 0 0);
  }
  & .apexcharts-tooltip {
    border: 0.5px solid grey !important;
    background: var(--expenses-dashboard-custom-back) !important;
    color: var(--expenses-main-text-color) !important;
  }
  & .apexcharts-tooltip-title {
    background: transparent !important;
    border-bottom: 0.5px solid grey !important;
  }
  & .apexcharts-xaxis-label,
  & .apexcharts-yaxis-label {
    fill: var(--header-subtitle-color);
  }

  .apexcharts-tooltip {
    box-shadow: 0px 0px 24px 0px #2c3f581f !important;
    border: none !important;
    --arrow-margin: 50%;
    --border-radius: 12px;
    overflow: visible;

    &.always-show-tooltip {
      opacity: 1 !important;
      pointer-events: all !important;
    }

    &::after {
      content: "";
      position: absolute;
      border: 11px solid transparent;
      border-top: 11px solid var(--dashboard-card-background);
      bottom: -22px;
      left: var(--arrow-margin);
      transform: translateX(-50%);
    }
    .column-chart__tooltip {
      padding: 8px 20px;
      background-color: var(--dashboard-card-background);
      min-width: 188px;
      max-width: 250px;
      text-align: center;
      border-radius: var(--border-radius) !important;
      display: flex;
      flex-direction: column;

      &__header {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 5px;
      }

      &__circle {
        width: 8px;
        height: 8px;

        border-radius: 50%;
      }

      &__label {
        font-weight: 400;
        font-size: 14px;
        color: #8a9099;
      }
      &__value {
        font-weight: 400;
        font-size: 14px;
        color: #78839c;
        white-space: normal;
        word-wrap: break-word;
        height: fit-content;
        max-height: 60px;
        overflow-y: auto;
        margin-bottom: 0;
      }
    }
  }

  &__buttons {
    display: flex;
    width: 40px;
    justify-content: space-between;
    user-select: none;

    &-item {
      opacity: 0.5;
      cursor: default;
      color: var(--expenses-main-text-color);

      &.active {
        opacity: 1;
        cursor: pointer;
      }
    }
  }
}
</style>
